import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { studiesCopyEn } from "@patient-watch/i18n/webapp/en-GB/studiesCopy";
import { studiesCopyDe } from "@patient-watch/i18n/webapp/de-DE/studiesCopy";
import { studiesCopyDa } from "@patient-watch/i18n/webapp/da-DK/studiesCopy";
import { studiesCopyEnUs } from "@patient-watch/i18n/webapp/en-US/studiesCopy";
import { studiesCopyFr } from "@patient-watch/i18n/webapp/fr-FR/studiesCopy";
import { studiesCopyIt } from "@patient-watch/i18n/webapp/it-IT/studiesCopy";
import { studiesCopyEs } from "@patient-watch/i18n/webapp/es-ES/studiesCopy";

export interface StudiesCopy {
    studies: string;
    description: string;
    unauthorizedMessage: string;
    searchPlaceholder: string;
    searchAriaLabel: string;
    clearSearchAria: string;
    filterStudiesAria: string;
    studyFilterDescription: string;
    newStudy: string;
    noStudiesYet: string;
    noStudiesMatchSearch: string;
    columnStudyName: string;
    columnLabel: string;
    columnForms: string;
    columnTemplates: string;
    columnPlan: string;
    columnExpiry: string;
    columnDiaries: string;
    columnClinicians: string;
    columnStatus: string;
    columnActions: string;
    published: string;
    draft: string;
    open: string;
    invite: string;
    edit: string;
    openStudyAria: string;
    inviteStudyAria: string;
    editStudyAria: string;
    openTrackingAria: string;
    trackingDialogTitle: (studyName: string) => string;
    emptyLabel: string;
    templateCount: (count: number) => string;
    newStudyTitle: string;
    newStudyDescription: string;
    editStudyTitle: string;
    editStudyDescription: string;
    studySaved: string;
    failedToSaveStudy: string;
    saveStudy: string;
    saveChanges: string;
    studyName: string;
    studyNamePlaceholder: string;
    studyDescription: string;
    studyDescriptionHint: string;
    studyDescriptionPlaceholder: string;
    labelAccess: string;
    selectLabels: string;
    addLabel: string;
    labelName: string;
    labelNamePlaceholder: string;
    formAccess: string;
    selectForms: string;
    templates: string;
    inferredAccessTitle: string;
    inferredAccessDescription: string;
    inferredAccessEmpty: string;
    inferredEvents: string;
    inferredForms: string;
    inferredSchedules: string;
    inferredNone: string;
    customiseAccessTitle: string;
    customiseAccessHideTitle: string;
    customiseAccessDescription: string;
    loadingAccessOptions: string;
    billingPlan: string;
    selectPlan: string;
    expiryDate: string;
    pickDate: string;
    expiryDateMinMessage: string;
    expiryDateMaxMessage: string;
    expiryDateInvalidMessage: string;
    publishStudyForManagers: string;
    labelAccessSummary: string;
    formAccessSummary: string;
    templatesSummary: string;
    billingPlanSummary: string;
    expirySummary: string;
    statusSummary: string;
    loadingStudy: string;
    studyNotFound: string;
    backToStudies: string;
    studyInvite: string;
    inviteToStudy: (studyName: string) => string;
    inviteToStudyDescription: string;
    inviteStepReview: string;
    inviteStepEmail: string;
    inviteStepQr: string;
    inviteStepInvite: string;
    generatingInviteLink: string;
    failedToCreateInviteLink: string;
    studyInviteQrDescription: (studyName: string) => string;
    joinStudyTitle: (studyName: string) => string;
    optionalOrganisationSignupHint: string;
    clinicianWillChooseOrganisation: string;
    specialty: string;
    optionalSuffix: string;
    optionalSpecialtySignupHint: string;
    selectSpecialtyPlaceholder: string;
    clearSpecialty: string;
    clinicianInviteDefaultsTitle: string;
    clinicianInviteDefaultsDescription: string;
    defaultOrganisation: string;
    noOrganisationsAvailable: string;
    inviteDefaultsSummary: (summary: string) => string;
    inviteDefaultsEmpty: string;
    emailRequired: string;
    validEmailRequired: string;
    studyInviteEmailSent: string;
    failedToSendInvite: string;
    trackingTitle: string;
    trackingDescription: string;
    trackingLabelsHint: string;
    trackingLoading: string;
    trackingError: string;
    trackingNoLabels: string;
    trackingNoClinicians: string;
    trackingNoDiaries: string;
    trackingTotalDiaries: string;
    trackingAvgDiariesPerClinician: string;
    trackingCliniciansWithDiaries: string;
    trackingDiariesPerClinician: string;
    trackingCumulativeDiaries: string;
    trackingColumnMember: string;
    trackingColumnDiaries: string;
    trackingColumnRole: string;
    trackingColumnTemplates: string;
    trackingColumnTrend: string;
    trackingColumnLastDiary: string;
    trackingLastDiaryNone: string;
    trackingTemplatesFromStudy: (count: number) => string;
    /** Organisation role keys (ADMIN, MANAGER, CLINICIAN, ...) to display labels. */
    trackingRoleLabels: Record<string, string>;
    trackingAddToStudy: string;
    trackingRemoveFromStudy: string;
    trackingAddAllToStudy: string;
    trackingCliniciansInStudy: string;
    trackingCliniciansInStudyHint: (total: number) => string;
    trackingStatusNotInStudy: string;
    trackingReasonHasLabels: string;
    trackingReasonHasDiaries: string;
    trackingNotInStudyCallout: (count: number) => string;
    trackingRowActions: (name: string) => string;
    trackingRemoveConfirmTitle: (name: string) => string;
    trackingRemoveConfirmDescription: string;
    trackingCancel: string;
    trackingCliniciansShort: string;
    trackingNoLabelsShort: string;
    trackingNoDiariesShort: string;
}

const studiesCopy: Record<SupportedLocale, StudiesCopy> = {
    "en-GB": studiesCopyEn,
    "de-DE": studiesCopyDe,
    "da-DK": studiesCopyDa,
    "en-US": studiesCopyEnUs,
    "fr-FR": studiesCopyFr,
    "it-IT": studiesCopyIt,
    "es-ES": studiesCopyEs,
};

export function useStudiesCopy(): StudiesCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(studiesCopy, locale);
}

export function getStudiesCopy(locale: SupportedLocale): StudiesCopy {
    return pickAppCopy(studiesCopy, locale);
}
